import {
  Children,
  createContext,
  createElement,
  forwardRef,
  Fragment,
  useContext,
  type ComponentType,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';

import { withLegacyMultiSelectLabel } from '../composites/MultiSelect/resolve-field-label';
import { InlineCopyBoundary } from '../lib/inline-copy';
import { EDITOR_HOST_CLASS_PROP, EDITOR_ROOT_REF_PROP } from './editor-host';
import { CatalogEditingContext } from './editing-context';
import { isUnsetOptionalEnum } from './optional-enum-props';
import type {
  CatalogAllowedComponent,
  CatalogPropDefinition,
  CatalogSlotDefinition,
  ComponentCatalog,
  ComponentCatalogEntry,
  InlineSubComponent,
  JsonObject,
  JsonValue,
} from './types';
import {
  childrenSlotCollisionAxis,
  SlotHost,
  shouldCollectSlotChild,
  shouldRenderInline,
  shouldResolveSlotChildren,
  takesEditorRootRef,
  withSlotChildRegistration,
  type PuckSlotCollisionAxis,
  type ResolveSlotComponent,
} from './slot-host';

type BarrelExports = Record<string, unknown>;
type PuckOptionValue = string | number | boolean;

interface PuckFieldBase {
  label: string;
  required?: boolean;
  /**
   * Puck's open metadata bag. `help` carries the catalog prop description so
   * editors can render operator help text beside the short `label`.
   */
  metadata?: { help?: string };
}

export interface PuckTextField extends PuckFieldBase {
  type: 'text';
  contentEditable?: boolean;
}

export interface PuckNumberField extends PuckFieldBase {
  type: 'number';
}

export interface PuckRadioField extends PuckFieldBase {
  type: 'radio';
  options: Array<{ label: string; value: PuckOptionValue }>;
}

export interface PuckSelectField extends PuckFieldBase {
  type: 'select';
  options: Array<{ label: string; value: PuckOptionValue }>;
}

export interface PuckSlotField extends PuckFieldBase {
  type: 'slot';
  allow?: string[];
  disallow?: string[];
  minEmptyHeight?: number;
}

export interface PuckTextareaField extends PuckFieldBase {
  type: 'textarea';
}

export interface PuckObjectField extends PuckFieldBase {
  type: 'object';
  objectFields: Record<string, PuckField>;
}

export interface PuckArrayField extends PuckFieldBase {
  type: 'array';
  /** Present for object-item arrays; drives Puck's per-item field editor. */
  arrayFields?: Record<string, PuckField>;
  defaultItemProps?: JsonObject;
  getItemSummary?: (item: unknown, index?: number) => string;
}

export type PuckField =
  | PuckTextField
  | PuckTextareaField
  | PuckNumberField
  | PuckRadioField
  | PuckSelectField
  | PuckSlotField
  | PuckObjectField
  | PuckArrayField;

export interface PuckComponentConfig {
  label: string;
  fields: Record<string, PuckField>;
  defaultProps: JsonObject;
  /**
   * Puck renders the component without its item wrapper `div` and expects
   * `render` to put `props.puck.dragRef` on the component's own element.
   */
  inline?: boolean;
  render: (props: Record<string, unknown>) => ReactNode;
}

export interface PuckCategoryConfig {
  components: string[];
}

export interface PuckConfig {
  categories: Record<string, PuckCategoryConfig>;
  components: Record<string, PuckComponentConfig>;
  root: {
    render: (props: { children?: ReactNode }) => ReactNode;
  };
}

export interface CreatePuckConfigOptions {
  useCache?: boolean;
  /** Fired when an entry is skipped because `exportName` is missing from `barrelExports`. */
  onMissingBarrelExport?: (entry: ComponentCatalogEntry) => void;
  /**
   * Fired when a later catalog entry reuses an `exportName` already registered.
   * The later entry wins; the previous component config and category placement are replaced.
   */
  onDuplicateExportName?: (
    previous: ComponentCatalogEntry,
    winner: ComponentCatalogEntry,
  ) => void;
}

const categoryLabels: Record<ComponentCatalogEntry['category'], string> = {
  content: 'Content',
  data: 'Data',
  form: 'Form',
  layout: 'Layout',
  navigation: 'Navigation',
  subcomponent: 'Subcomponent',
};

const addToCategory = (
  categories: Record<string, PuckCategoryConfig>,
  categoryLabel: string,
  exportName: string,
): void => {
  const bucket = categories[categoryLabel] ?? { components: [] };
  if (!bucket.components.includes(exportName)) {
    bucket.components.push(exportName);
  }
  categories[categoryLabel] = bucket;
};

const PUCK_HIDDEN_PROPS = new Set([
  'asChild',
  'className',
  'ref',
  'key',
  'reserveBackground',
  'editorRootRef',
]);

const isHiddenFromPuck = (propName: string): boolean =>
  PUCK_HIDDEN_PROPS.has(propName) || /[Cc]lassName$/.test(propName);

let puckConfigCache = new WeakMap<
  ComponentCatalog,
  WeakMap<BarrelExports, PuckConfig>
>();

/**
 * Operator-facing names for prop keys whose humanized form is an abbreviation
 * an analyst would not recognise ("Href", "Cta"). Used only for the fallback
 * path: an authored `prop.label` always wins. Keyed by lowercased prop name.
 */
const FALLBACK_LABELS: Record<string, string> = {
  alt: 'Image description',
  cta: 'Call to action',
  ctalabel: 'Button text',
  ctatext: 'Button text',
  ctaurl: 'Link URL',
  href: 'Link URL',
  id: 'Identifier',
  imgsrc: 'Image URL',
  src: 'Image URL',
  url: 'Link URL',
};

const humanize = (value: string): string => {
  const known = FALLBACK_LABELS[value.toLowerCase()];
  if (known) return known;

  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const resolveComponent = (
  exportName: string,
  barrelExports: BarrelExports,
): ComponentType<Record<string, unknown>> | null => {
  const component = barrelExports[exportName];

  return component
    ? (component as ComponentType<Record<string, unknown>>)
    : null;
};

const findEntryById = (
  catalog: ComponentCatalog,
  componentId: string,
): ComponentCatalogEntry | undefined =>
  catalog.find((entry) => entry.id === componentId);

const resolveAllowedExportName = (
  component: CatalogAllowedComponent,
  catalog: ComponentCatalog,
): string | undefined => {
  if (typeof component === 'string') {
    return findEntryById(catalog, component)?.exportName;
  }
  return component.exportName;
};

const SLOT_MIN_EMPTY_HEIGHT = 200;
const COMPOSABLE_STRING_SLOT_MIN_EMPTY_HEIGHT = 48;

const isComposableStringSlot = (
  slotDefinition: CatalogSlotDefinition,
): boolean =>
  slotDefinition.type === 'string' &&
  Boolean(slotDefinition.allowedComponents?.length);

const toSlotField = (
  label: string,
  slotDefinition: CatalogSlotDefinition,
  catalog: ComponentCatalog,
): PuckSlotField => {
  const allow = slotDefinition.allowedComponents
    ?.map((c) => resolveAllowedExportName(c, catalog))
    .filter((name): name is string => Boolean(name));
  const disallow = slotDefinition.disallowedComponents
    ?.map((id) => findEntryById(catalog, id)?.exportName)
    .filter((name): name is string => Boolean(name));

  const help = slotDefinition.description?.trim();

  return {
    type: 'slot',
    label,
    ...(help ? { metadata: { help } } : {}),
    ...(allow?.length ? { allow } : {}),
    ...(disallow?.length ? { disallow } : {}),
    minEmptyHeight: isComposableStringSlot(slotDefinition)
      ? COMPOSABLE_STRING_SLOT_MIN_EMPTY_HEIGHT
      : SLOT_MIN_EMPTY_HEIGHT,
  };
};

const MODAL_SHADOW_LABELS: Record<string, string> = {
  none: 'None',
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  xl: 'Extra large',
};

const PILL_BUTTON_LABELS: Record<string, Record<string, string>> = {
  weight: {
    regular: 'Regular',
    medium: 'Medium',
    semibold: 'Semibold',
    bold: 'Bold',
  },
  size: { sm: 'Small', md: 'Medium' },
  variant: { filled: 'Filled', outline: 'Outline', ghost: 'Ghost' },
  color: {
    neutral: 'Neutral',
    primary: 'Primary',
    secondary: 'Secondary',
    accent: 'Accent',
  },
  rounded: { full: 'Pill', sm: 'Rounded', none: 'Square' },
  padding: { sm: 'Small', md: 'Medium', lg: 'Large' },
  hover: { lighter: 'Lighter', darker: 'Darker', none: 'None' },
};

const HEADING_COLOR_LABELS: Record<string, string> = {
  'category-1': 'Data visualization - Category 1',
  'category-2': 'Data visualization - Category 2',
  'category-3': 'Data visualization - Category 3',
};

const formatEnumOptionLabel = (
  propName: string,
  value: string | number,
  exportName?: string,
): string => {
  if (exportName === 'PillButton') {
    const pillLabel = PILL_BUTTON_LABELS[propName]?.[String(value)];
    if (pillLabel) return pillLabel;
  }
  if (propName === 'lines' && value === 'none') return 'No limit';
  if (propName === 'scope' && value === 'col') return 'Column';
  if (propName === 'scope' && value === 'row') return 'Row';
  if (propName === 'size' && value === 'lg') return 'LG';
  if (propName === 'target' && value === '_self') return 'Opens in same tab';
  if (propName === 'target' && value === '_blank') return 'Opens in new tab';
  if (exportName === 'Heading' && propName === 'color') {
    const headingColorLabel = HEADING_COLOR_LABELS[String(value)];
    if (headingColorLabel) return headingColorLabel;
  }
  if (exportName === 'Modal') {
    if (propName === 'variant' && value === 'default') return 'Centered';
    if (propName === 'variant' && value === 'fullscreen') return 'Fullscreen';
    if (propName === 'variant' && value === 'sheet') return 'Sheet';
    if (propName === 'panelSide' && value === 'left') return 'Left';
    if (propName === 'panelSide' && value === 'right') return 'Right';
    if (propName === 'backdrop' && value === 'default') return 'Black';
    if (propName === 'backdrop' && value === 'tinted') return 'Brand tint';
    if (propName === 'shadow') {
      const shadowLabel = MODAL_SHADOW_LABELS[String(value)];
      if (shadowLabel) return shadowLabel;
    }
  }
  return String(value);
};

/**
 * Maps a catalog prop to a Puck field based on its declared type.
 * Slots are handled separately via the entry's explicit `slots` record.
 */
const mapPropToField = (
  propName: string,
  prop: CatalogPropDefinition,
  exportName?: string,
): PuckField => {
  const label = prop.label || humanize(propName);
  const help = prop.description?.trim();
  const meta = help ? { metadata: { help } } : {};

  switch (prop.type) {
    case 'string':
      return { type: 'text', label, required: prop.required, ...meta };
    case 'number':
      return { type: 'number', label, required: prop.required, ...meta };
    case 'boolean':
      return {
        type: 'radio',
        label,
        required: prop.required,
        ...meta,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      };
    case 'enum': {
      const options: Array<{ label: string; value: PuckOptionValue }> = (
        prop.enumValues ?? []
      ).map((v) => ({
        label: formatEnumOptionLabel(propName, v, exportName),
        value: v,
      }));
      if (prop.required !== true) {
        options.unshift({ label: 'Default', value: '' });
      }
      return {
        type: 'select',
        label,
        required: prop.required,
        ...meta,
        options,
      };
    }
    case 'object': {
      if (prop.objectFields) {
        return {
          type: 'object',
          label,
          required: prop.required,
          ...meta,
          objectFields: Object.fromEntries(
            Object.entries(prop.objectFields)
              .filter(([k]) => !isHiddenFromPuck(k))
              .map(([k, v]) => [k, mapPropToField(k, v, exportName)]),
          ),
        };
      }
      return {
        type: 'textarea',
        label,
        required: prop.required,
        ...meta,
      };
    }
    case 'array': {
      const item = prop.arrayItem;
      if (item?.type === 'object') {
        const arrayFields = Object.fromEntries(
          Object.entries(item.objectFields)
            .filter(([k]) => !isHiddenFromPuck(k))
            .map(([k, v]) => [k, mapPropToField(k, v, exportName)]),
        );
        return {
          type: 'array',
          label,
          required: prop.required,
          ...meta,
          arrayFields,
          defaultItemProps: buildArrayItemDefaults(arrayFields),
          getItemSummary: item.rowLabel
            ? buildArrayItemSummarizer(item.rowLabel)
            : summarizeArrayItem,
        };
      }
      // Scalar-item arrays (e.g. `string[]`) and arrays with no declared item
      // schema have no native Puck array editor. Fall back to a textarea;
      // editors with scalar-array support key off the catalog's `arrayItem`
      // marker to swap in a dedicated repeatable-input field.
      return {
        type: 'textarea',
        label,
        required: prop.required,
        ...meta,
      };
    }
    default:
      return {
        type: 'textarea',
        label,
        required: prop.required,
        ...meta,
      };
  }
};

/** Empty per-field defaults for a new object array item, keyed by field type. */
const buildArrayItemDefaults = (
  arrayFields: Record<string, PuckField>,
): JsonObject => {
  const defaults: JsonObject = {};
  for (const [key, field] of Object.entries(arrayFields)) {
    switch (field.type) {
      case 'number':
        defaults[key] = 0;
        break;
      case 'radio':
        defaults[key] = false;
        break;
      case 'array':
        defaults[key] = [];
        break;
      case 'object':
        defaults[key] = {};
        break;
      default:
        defaults[key] = '';
    }
  }
  return defaults;
};

const ARRAY_ITEM_SUMMARY_KEYS = [
  'label',
  'title',
  'name',
  'value',
  'alt',
] as const;

/** Prefers a human-readable field for the Puck array-item row summary. */
const summarizeArrayItem = (item: unknown, index?: number): string => {
  if (item && typeof item === 'object' && !Array.isArray(item)) {
    const record = item as Record<string, unknown>;
    for (const key of ARRAY_ITEM_SUMMARY_KEYS) {
      const value = record[key];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
  }
  return `Item ${(index ?? 0) + 1}`;
};

/**
 * Rows of an array that names itself are numbered under that noun instead of
 * taking a summary from their own fields, so sibling arrays stay distinct.
 */
const buildArrayItemSummarizer =
  (rowLabel: string) =>
  (_item: unknown, index?: number): string =>
    `${rowLabel} ${(index ?? 0) + 1}`;

const isJsonObject = (value: JsonValue | undefined): value is JsonObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** Collection defaults compete with SlotHost children (items[] vs compound). */
const isCollectionDefault = (
  value: JsonValue,
  prop?: CatalogPropDefinition,
): boolean => prop?.type === 'array' || Array.isArray(value);

const filterObjectDefaultValue = (
  value: JsonValue,
  objectFields: Record<string, CatalogPropDefinition> | undefined,
): JsonValue => {
  if (!objectFields || !isJsonObject(value)) return value;

  const filtered: JsonObject = {};
  for (const [key, nestedValue] of Object.entries(value)) {
    if (isHiddenFromPuck(key)) continue;

    const nestedProp = objectFields[key];
    filtered[key] =
      nestedProp?.type === 'object'
        ? filterObjectDefaultValue(nestedValue, nestedProp.objectFields)
        : nestedValue;
  }

  return filtered;
};

const getEditableDefaultValue = (
  prop: CatalogPropDefinition,
  value: JsonValue,
): JsonValue =>
  prop.type === 'object'
    ? filterObjectDefaultValue(value, prop.objectFields)
    : value;

/**
 * Slot keys are owned by the editor (Puck); static defaults must not collide.
 * SlotHost canvas path is children, not a competing items[] API — omit
 * collection defaults when the entry resolves slot children.
 */
const buildDefaultProps = (
  entry: ComponentCatalogEntry,
  slotPropNames: ReadonlySet<string>,
): JsonObject => {
  const omitCollectionDefaults = shouldResolveSlotChildren(
    entry.exportName,
    entry.resolveSlotChildren,
  );
  const defaults: JsonObject = {};

  for (const [name, prop] of Object.entries(entry.props)) {
    if (name === 'children') continue;
    if (slotPropNames.has(name)) continue;
    if (isHiddenFromPuck(name)) continue;
    if (prop.default === undefined) continue;
    if (omitCollectionDefaults && isCollectionDefault(prop.default, prop)) {
      continue;
    }
    defaults[name] = getEditableDefaultValue(prop, prop.default);
  }

  if (entry.defaultProps && isJsonObject(entry.defaultProps)) {
    for (const [key, value] of Object.entries(entry.defaultProps)) {
      if (key === 'children') continue;
      if (slotPropNames.has(key)) continue;
      if (isHiddenFromPuck(key)) continue;
      const prop = entry.props[key];
      if (omitCollectionDefaults && isCollectionDefault(value, prop)) {
        continue;
      }
      defaults[key] = prop ? getEditableDefaultValue(prop, value) : value;
    }
  }

  return defaults;
};

/**
 * Wrapper component handed to Puck's `DropZoneRender` via its `as` prop so
 * Puck skips its default `<div>` around slot children. Puck's internals pass
 * `{ className, style, ref, children }` to whatever component is supplied
 * via `as`; everything except `children` is intentionally discarded so the
 * host primitive (e.g. Flex, Select) receives its nested items as direct
 * children — preserving flexbox layout, Radix listbox semantics, and
 * keeping the DOM free of layout-invisible wrappers.
 *
 * `forwardRef` is used purely to absorb the ref Puck forwards; the wrapper
 * itself has nothing to attach it to.
 */
export const SlotPassthrough = forwardRef<unknown, { children?: ReactNode }>(
  function SlotPassthrough({ children }, _ref) {
    return createElement(Fragment, null, children);
  },
);

type SlotHostBridge = {
  component: ComponentType<Record<string, unknown>>;
  props: Record<string, unknown>;
};

const SlotHostBridgeContext = createContext<SlotHostBridge | null>(null);

/**
 * Puck slot `as` target that renders the catalog component itself.
 * The drop-target ref, class, data attributes, and empty min-height stay on
 * that element, and the slot items are its direct children. The Puck class
 * is also passed as `editorHostClassName` so components that omit public
 * `className` can still keep the drop-zone rule.
 */
export const CatalogSlotHost = forwardRef<unknown, { children?: ReactNode }>(
  function CatalogSlotHost(props, ref) {
    const { children, className, style, ...hostProps } = props as {
      children?: ReactNode;
      className?: string;
      style?: CSSProperties;
    } & Record<string, unknown>;
    const bridge = useContext(SlotHostBridgeContext);
    if (!bridge) {
      return createElement(Fragment, null, children);
    }
    return createElement(bridge.component, {
      ...bridge.props,
      ...hostProps,
      ref,
      style,
      className,
      [EDITOR_HOST_CLASS_PROP]: className,
      children,
    });
  },
);

type SlotAs = ComponentType<{ children?: ReactNode }>;

type SlotRenderFn = (props: {
  as?: SlotAs;
  collisionAxis?: PuckSlotCollisionAxis;
}) => ReactNode;

/**
 * Puck rebuilds each slot function with a new identity whenever its field
 * transforms recompute (e.g. selecting a block in the canvas). Mounting that
 * function as an element type remounts every slotted child, which discards
 * DOM-held state such as a native `<select>`'s chosen `<option>`. Invoking it
 * from a component with a stable type keeps the subtree reconciled in place.
 */
export const StableSlot = ({
  render,
  as,
  collisionAxis,
}: {
  render: SlotRenderFn;
  as?: SlotAs;
  collisionAxis?: PuckSlotCollisionAxis;
}): ReactNode =>
  render({
    ...(as ? { as } : {}),
    ...(collisionAxis ? { collisionAxis } : {}),
  });

const stableSlot = (
  value: unknown,
  as?: SlotAs,
  collisionAxis?: PuckSlotCollisionAxis,
): ReactNode =>
  createElement(StableSlot, {
    render: value as SlotRenderFn,
    as,
    collisionAxis,
  });

const renderSlotValue = (
  value: unknown,
  passthrough = false,
  collisionAxis?: PuckSlotCollisionAxis,
): ReactNode => {
  if (typeof value === 'function') {
    return stableSlot(
      value,
      passthrough ? SlotPassthrough : undefined,
      collisionAxis,
    );
  }
  if (value != null) {
    return value as ReactNode;
  }
  return null;
};

/**
 * Puck assigns the drop-target ref to whatever `as` renders. A fragment
 * cannot host that ref, so composable string slots use a phrasing `<span>`
 * instead of {@link SlotPassthrough}. An empty slot is `inline-block` so
 * authors have a measurable target; composed copy stays inline.
 */
export const InlineSlotElement = forwardRef<
  HTMLSpanElement,
  { children?: ReactNode; style?: CSSProperties }
>(function InlineSlotElement({ children, style, ...rest }, ref) {
  const isEmpty = Children.count(children) === 0;
  return createElement(
    InlineCopyBoundary,
    null,
    createElement(
      'span',
      {
        ...rest,
        ref,
        style: isEmpty
          ? { ...style, display: 'inline-block', minWidth: '3rem' }
          : style,
      },
      children,
    ),
  );
});
InlineSlotElement.displayName = 'InlineSlotElement';

/**
 * Block-level `as` target for a named slot that must accept drops while it
 * is empty. Puck puts the drop-target ref and empty min-height on it.
 */
export const BlockSlotElement = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & Record<`data-${string}`, string>
>(function BlockSlotElement(props, ref) {
  return createElement('div', { ...props, ref });
});

const isPuckEditing = (props: Record<string, unknown>): boolean => {
  const puck = props.puck;
  return (
    !!puck &&
    typeof puck === 'object' &&
    (puck as { isEditing?: boolean }).isEditing === true
  );
};

/** Puck's drag ref for the current item, when Puck supplies one. */
const readPuckDragRef = (
  props: Record<string, unknown>,
): Ref<Element> | undefined => {
  const puck = props['puck'];
  if (puck === null || typeof puck !== 'object') return undefined;
  const dragRef = (puck as { dragRef?: unknown }).dragRef;
  if (typeof dragRef === 'function') return dragRef as Ref<Element>;
  if (dragRef !== null && typeof dragRef === 'object' && 'current' in dragRef) {
    return dragRef as Ref<Element>;
  }
  return undefined;
};

const createComponentRender = (
  component: ComponentType<Record<string, unknown>>,
  knownPropNames: ReadonlySet<string>,
  slotNames: ReadonlySet<string>,
  inlineSlotNames: ReadonlySet<string>,
  entryCategory: ComponentCatalogEntry['category'],
  options?: {
    passthroughChildren?: boolean;
    hostChildren?: boolean;
    resolveSlotChildren?: boolean;
    resolveBarrel?: ResolveSlotComponent;
    /** Copy a saved MultiSelect `label` onto `fieldLabel` before unknown props are dropped. */
    copyLegacyLabel?: boolean;
    /** Catalog props for this component. Used to drop an unset optional enum. */
    catalogProps?: Record<string, CatalogPropDefinition>;
    /** Named slots rendered through {@link BlockSlotElement} while editing. */
    editorBlockSlotNames?: ReadonlySet<string>;
    /** Puck `inline` component: its own element takes Puck's drag ref. */
    inline?: boolean;
    /** Pass the drag ref as the editor root ref instead of `ref`. */
    takesEditorRootRef?: boolean;
    /** Drag axis for the items of the children slot. */
    childrenCollisionAxis?: PuckSlotCollisionAxis;
  },
) => {
  const shouldPassthroughChildren =
    options?.passthroughChildren === true ||
    (entryCategory === 'layout' && slotNames.size > 0);
  const inline = options?.inline === true;
  // Both paths render a wrapper around the component, which cannot take the
  // drag ref an inline component needs on its own element.
  const hostChildren = !inline && options?.hostChildren === true;
  const resolveSlotChildren = !inline && options?.resolveSlotChildren === true;
  const resolveBarrel = options?.resolveBarrel;
  const catalogProps = options?.catalogProps;
  const editorBlockSlotNames = options?.editorBlockSlotNames;
  const childrenCollisionAxis = options?.childrenCollisionAxis;
  const dragRefProp =
    options?.takesEditorRootRef === true ? EDITOR_ROOT_REF_PROP : 'ref';

  return (props: Record<string, unknown>): ReactNode => {
    const sourceProps = options?.copyLegacyLabel
      ? withLegacyMultiSelectLabel(props)
      : props;
    const componentProps: Record<string, unknown> = {};
    const slotValues: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(sourceProps)) {
      if (slotNames.has(key)) {
        slotValues[key] = value;
      } else if (knownPropNames.has(key)) {
        if (isUnsetOptionalEnum(catalogProps?.[key], value)) continue;
        // The drag ref is applied below, and only while editing. A saved
        // value here is not a ref and would throw when Preview assigns it.
        if (key === EDITOR_ROOT_REF_PROP) continue;
        componentProps[key] = value;
      }
    }

    const childrenSlot = slotValues['children'];
    const { ...otherSlotValues } = slotValues;
    delete otherSlotValues['children'];

    const renderedNamedSlots: Record<string, ReactNode> = {};
    const isEditing = isPuckEditing(sourceProps);
    for (const [key, value] of Object.entries(otherSlotValues)) {
      if (inlineSlotNames.has(key) && typeof value === 'function') {
        renderedNamedSlots[key] = stableSlot(value, InlineSlotElement);
      } else if (
        isEditing &&
        editorBlockSlotNames?.has(key) &&
        typeof value === 'function'
      ) {
        renderedNamedSlots[key] = stableSlot(value, BlockSlotElement);
      } else {
        renderedNamedSlots[key] = renderSlotValue(value, true);
      }
    }

    const dragRef = inline ? readPuckDragRef(props) : undefined;
    const passthroughProps = {
      ...componentProps,
      ...renderedNamedSlots,
      ...(dragRef ? { [dragRefProp]: dragRef } : {}),
    };

    if (childrenSlot !== undefined) {
      if (hostChildren && typeof childrenSlot === 'function') {
        return createElement(
          SlotHostBridgeContext.Provider,
          { value: { component, props: passthroughProps } },
          stableSlot(childrenSlot, CatalogSlotHost),
        );
      }

      if (
        resolveSlotChildren &&
        typeof childrenSlot === 'function' &&
        resolveBarrel
      ) {
        return createElement(SlotHost, {
          Host: component,
          hostProps: passthroughProps,
          slot: childrenSlot as ComponentType<{
            as?: ComponentType<{ children?: ReactNode }>;
          }>,
          resolveComponent: resolveBarrel,
          mode: 'element',
        });
      }

      if (
        (shouldPassthroughChildren || inlineSlotNames.has('children')) &&
        typeof childrenSlot === 'function'
      ) {
        return createElement(
          component,
          passthroughProps,
          stableSlot(
            childrenSlot,
            inlineSlotNames.has('children')
              ? InlineSlotElement
              : SlotPassthrough,
            childrenCollisionAxis,
          ),
        );
      }

      return createElement(
        component,
        passthroughProps,
        renderSlotValue(childrenSlot, false, childrenCollisionAxis),
      );
    }

    return createElement(component, passthroughProps);
  };
};

/**
 * The editor canvas has no career data, and the published default hides the
 * panel in that case. Force the empty panel visible while editing. The saved
 * prop is left unchanged for the published runtime.
 */
const showEmptyRecommendationsInEditor = (
  render: (props: Record<string, unknown>) => ReactNode,
) => {
  return (props: Record<string, unknown>): ReactNode => {
    if (!isPuckEditing(props)) {
      return render(props);
    }

    return render({ ...props, hideWhenEmpty: false });
  };
};

/**
 * Export names that read {@link CatalogEditingContext}. Navbar and Popover
 * keep an opened menu or panel open on the canvas so authors can drop content
 * into it. ShowMore leaves every item visible so the list does not collapse to
 * an empty box while someone is editing it.
 */
const EDITING_AWARE_EXPORT_NAMES: ReadonlySet<string> = new Set([
  'Navbar',
  'Popover',
  'ShowMore',
]);

/**
 * Named slots that get a block drop target on the editor canvas, so authors
 * can drop into them while empty. Published pages keep the fragment, so an
 * empty slot leaves no markup.
 */
const EDITOR_BLOCK_SLOTS: Readonly<Record<string, ReadonlySet<string>>> = {
  Navbar: new Set(['menuContent', 'actions']),
  Carousel: new Set(['header']),
  Popover: new Set(['header', 'body', 'footer']),
};

const provideEditingState = (
  render: (props: Record<string, unknown>) => ReactNode,
) => {
  return (props: Record<string, unknown>): ReactNode => {
    if (!isPuckEditing(props)) {
      return render(props);
    }

    return createElement(
      CatalogEditingContext.Provider,
      { value: true },
      render(props),
    );
  };
};

const getCachedConfig = (
  catalog: ComponentCatalog,
  barrelExports: BarrelExports,
): PuckConfig | undefined => puckConfigCache.get(catalog)?.get(barrelExports);

const setCachedConfig = (
  catalog: ComponentCatalog,
  barrelExports: BarrelExports,
  config: PuckConfig,
): void => {
  const byBarrel = puckConfigCache.get(catalog) ?? new WeakMap();
  byBarrel.set(barrelExports, config);
  puckConfigCache.set(catalog, byBarrel);
};

export const clearPuckConfigCache = (): void => {
  puckConfigCache = new WeakMap();
};

const isInlineSubComponent = (
  c: CatalogAllowedComponent,
): c is InlineSubComponent => typeof c !== 'string';

/**
 * Scans a slot definition for inline sub-component objects and registers
 * each one as a first-class Puck component. The sub-component gets a
 * single `children` slot whose `allow` list is resolved from
 * `config.allowedComponents`.
 */
const registerInlineSubComponents = (
  slotDef: CatalogSlotDefinition,
  catalog: ComponentCatalog,
  barrelExports: BarrelExports,
  components: Record<string, PuckComponentConfig>,
  categories: Record<string, PuckCategoryConfig>,
): void => {
  for (const allowed of slotDef.allowedComponents ?? []) {
    if (!isInlineSubComponent(allowed)) continue;

    const { exportName, displayName, config } = allowed;
    if (components[exportName]) continue;

    const component = resolveComponent(exportName, barrelExports);
    if (!component) continue;

    const childrenAllow = config?.allowedComponents
      ?.map((id) => findEntryById(catalog, id)?.exportName)
      .filter((name): name is string => Boolean(name));

    const fields: Record<string, PuckField> = {
      children: {
        type: 'slot' as const,
        label: `${displayName} content`,
        ...(childrenAllow?.length ? { allow: childrenAllow } : {}),
        minEmptyHeight: SLOT_MIN_EMPTY_HEIGHT,
      },
    };

    components[exportName] = {
      label: displayName,
      fields,
      defaultProps: {},
      render: createComponentRender(
        component,
        new Set<string>(),
        new Set(['children']),
        new Set<string>(),
        'subcomponent',
      ),
    };

    addToCategory(categories, 'Subcomponent', exportName);
  }
};

/**
 * Converts an EDU Component Catalog into a Puck editor configuration.
 *
 * The caller provides barrel exports so this module stays decoupled from
 * any specific component library bundle. Field types are derived
 * exclusively from catalog metadata, with no component patching.
 * Puck-only internal fields (`asChild`, `className`, `ref`, `key`,
 * `editorRootRef`, and `*ClassName`) are filtered from editable fields and
 * defaults while staying present in the shared catalog contract.
 *
 * **Slots:** Keys in `entry.slots` override same-named props (no duplicate
 * fields). Slot keys are omitted from `defaultProps` so the editor owns
 * slot state. Function-valued **named** slots (e.g. Badge `iconBefore` /
 * `iconAfter`) always render through `SlotPassthrough` so compact hosts
 * keep direct children.
 * For `category: 'layout'`, or when a `children` slot declares
 * `passthrough: true`, a function-valued `children` slot also uses
 * `SlotPassthrough` so the host sees direct children without Puck's default
 * wrapper element. Other non-layout `children` slots retain the wrapper.
 * A `children` slot with `host: true` renders the component as the drop area,
 * ahead of `resolveSlotChildren`. Catalog entries with `resolveSlotChildren:
 * true` and no host invert the slot through {@link SlotHost} so parents that
 * `Children.toArray` their children receive real component elements.
 * Accordion and other mount-the-slot hosts stay on the default path.
 *
 * **Caching:** When `useCache` is true (default), results are memoized by
 * `catalog` and `barrelExports` object identity. Treat the catalog array
 * as immutable after passing it in, or call {@link clearPuckConfigCache} /
 * use `useCache: false` after mutations.
 *
 * **Duplicates:** If multiple entries share the same `exportName`, the
 * last one in array order wins. Optional {@link CreatePuckConfigOptions.onDuplicateExportName}
 * reports collisions.
 */
export function createPuckConfig(
  catalog: ComponentCatalog,
  barrelExports: BarrelExports,
  options?: CreatePuckConfigOptions,
): PuckConfig {
  const useCache = options?.useCache ?? true;
  const onMissingBarrelExport = options?.onMissingBarrelExport;
  const onDuplicateExportName = options?.onDuplicateExportName;

  if (useCache) {
    const cached = getCachedConfig(catalog, barrelExports);
    if (cached) return cached;
  }

  const components: Record<string, PuckComponentConfig> = {};
  const categories: Record<string, PuckCategoryConfig> = {};
  const exportNameToEntry = new Map<string, ComponentCatalogEntry>();
  const resolveBarrel: ResolveSlotComponent = (exportName) =>
    resolveComponent(exportName, barrelExports);

  for (const entry of catalog) {
    const component = resolveComponent(entry.exportName, barrelExports);
    if (!component) {
      onMissingBarrelExport?.(entry);
      continue;
    }

    const previous = exportNameToEntry.get(entry.exportName);
    if (previous) {
      onDuplicateExportName?.(previous, entry);
      const prevLabel = categoryLabels[previous.category];
      const prevBucket = categories[prevLabel];
      if (prevBucket) {
        prevBucket.components = prevBucket.components.filter(
          (name) => name !== entry.exportName,
        );
        if (prevBucket.components.length === 0) {
          delete categories[prevLabel];
        }
      }
    }

    const fields: Record<string, PuckField> = {};
    const slotNames = new Set<string>(Object.keys(entry.slots ?? {}));

    const textSlotNames = new Set<string>();
    const inlineSlotNames = new Set<string>();
    for (const [slotName, slotDef] of Object.entries(entry.slots ?? {})) {
      const label = slotDef.label || humanize(slotName);
      const slotHelp = slotDef.description?.trim();
      const slotMeta = slotHelp ? { metadata: { help: slotHelp } } : {};
      if (slotDef.type === 'string' && !isComposableStringSlot(slotDef)) {
        textSlotNames.add(slotName);
        fields[slotName] = {
          type: 'text',
          label,
          contentEditable: true,
          ...slotMeta,
        };
      } else {
        if (isComposableStringSlot(slotDef)) {
          inlineSlotNames.add(slotName);
        }
        registerInlineSubComponents(
          slotDef,
          catalog,
          barrelExports,
          components,
          categories,
        );
        fields[slotName] = toSlotField(label, slotDef, catalog);
      }
    }

    for (const [propName, prop] of Object.entries(entry.props)) {
      if (propName === 'children') continue;
      if (slotNames.has(propName)) continue;
      if (isHiddenFromPuck(propName)) continue;
      // Saved MultiSelect `label` stays readable, but it is not an editor field.
      if (entry.exportName === 'MultiSelect' && propName === 'label') continue;
      fields[propName] = mapPropToField(propName, prop, entry.exportName);
    }

    const realSlotNames = new Set(
      Array.from(slotNames).filter((k) => !textSlotNames.has(k)),
    );

    const knownPropNames = new Set([
      ...Object.keys(entry.props).filter(
        (k) => k !== 'children' && !slotNames.has(k),
      ),
      ...Array.from(textSlotNames),
    ]);

    const inline = shouldRenderInline(entry.exportName);
    const componentRender = createComponentRender(
      component,
      knownPropNames,
      realSlotNames,
      inlineSlotNames,
      entry.category,
      {
        passthroughChildren: entry.slots?.children?.passthrough === true,
        hostChildren: entry.slots?.children?.host === true,
        resolveSlotChildren: shouldResolveSlotChildren(
          entry.exportName,
          entry.resolveSlotChildren,
        ),
        resolveBarrel,
        copyLegacyLabel: entry.exportName === 'MultiSelect',
        catalogProps: entry.props,
        editorBlockSlotNames: EDITOR_BLOCK_SLOTS[entry.exportName],
        inline,
        takesEditorRootRef: takesEditorRootRef(entry.exportName),
        childrenCollisionAxis: childrenSlotCollisionAxis(entry.exportName),
      },
    );
    let render = componentRender;
    if (entry.exportName === 'CareerExplorerRecommendations') {
      render = showEmptyRecommendationsInEditor(componentRender);
    } else if (EDITING_AWARE_EXPORT_NAMES.has(entry.exportName)) {
      render = provideEditingState(componentRender);
    }

    components[entry.exportName] = {
      label: entry.displayName,
      fields,
      defaultProps: buildDefaultProps(entry, realSlotNames),
      ...(inline ? { inline: true } : {}),
      render: shouldCollectSlotChild(entry.exportName)
        ? withSlotChildRegistration(render, entry.exportName)
        : render,
    };

    exportNameToEntry.set(entry.exportName, entry);

    addToCategory(categories, categoryLabels[entry.category], entry.exportName);
  }

  components['PuckText'] = {
    label: 'Text',
    fields: {
      text: { type: 'textarea', label: 'Text' },
      tag: {
        type: 'select',
        label: 'HTML Element',
        options: [
          { label: 'span', value: 'span' },
          { label: 'p', value: 'p' },
          { label: 'div', value: 'div' },
        ],
      },
    },
    defaultProps: { text: 'Text', tag: 'span' },
    render: (props: Record<string, unknown>) =>
      createElement(
        (props.tag as string) || 'span',
        null,
        (props.text as string) ?? '',
      ),
  };

  const config: PuckConfig = {
    categories,
    components,
    root: {
      render: ({ children }) => createElement('div', null, children),
    },
  };

  if (useCache) {
    setCachedConfig(catalog, barrelExports, config);
  }

  return config;
}
